// Defines the type that holds the timeline chart's state and the reducer function to manage it.

export interface TimeseriesEvent {
  time: Date;
  value: number | null;
}

export interface Threshold {
  key: string;
  name: string;
  value: number;
  timeseries: string[];
  unit?: string;
}

export interface ChartTimeline {
  id: string; // Unique identifier for the chart
  title: string; // Title of the chart, displayed in the legend
  scale: "interval" | "ratio"; // Decides how the y-axis is scaled and whether to show bar or line chart
  color: string;
  unit: string; // Unit of measurement including possible reference frame, e.g. "mm/h", "mNAP", used for the y-axis label
  data: TimeseriesEvent[];
  isLoading: boolean;
  thresholds: Threshold[];
}

export interface VerticalLine {
  id: string; // Unique identifier for the vertical line
  time: Date; // Time of the vertical line
  color: string; // Color for the vertical line
  width: number; // Width for the vertical line
  dash: Plotly.Dash; // Dash type for the vertical line
}

export interface TimelineChartState {
  start?: Date; // If no start and end are set, use the chart data's range
  end?: Date; // If no start and end are set, use the chart data's range
  large: boolean; // Whether the chart is displayed in a large format
  yAxes: string[]; // Array of y-axis identifiers, can be 1 or 2 elements
  charts: ChartTimeline[]; // Array of chart data
  verticalLines: VerticalLine[]; // Optional vertical lines to be displayed on the chart
  width: number; // Width of the chart
  height: number; // Height of the chart
  isCumulative: boolean; // Whether the chart data is cumulative
  showlegend: boolean; // Whether to show the legend
  legend?: Partial<Plotly.Legend>; // Optional legend configuration
  hoverdistance?: number; // Optional hover distance for tooltips
  datarevision: number; // Increase this whenever data changes
}

interface AddChartAction {
  type: "ADD_CHART";
  chart: ChartTimeline;
}

interface RemoveChartAction {
  type: "REMOVE_CHART";
  id: string; // ID of the chart to remove
}

interface AddVerticalLineAction {
  type: "ADD_VERTICAL_LINE";
  verticalLine: VerticalLine;
}

interface RemoveVerticalLineAction {
  type: "REMOVE_VERTICAL_LINE";
  id: string; // ID of the vertical line to remove
}

interface SetLegendAction {
  type: "SET_LEGEND";
  legend?: Partial<Plotly.Legend>;
}

type TimelineChartAction =
  | AddChartAction
  | RemoveChartAction
  | AddVerticalLineAction
  | RemoveVerticalLineAction
  | SetLegendAction;

export type TimelineChartDispatch = (action: TimelineChartAction) => void;

export function timelineChartReducer(
  state: TimelineChartState,
  action: TimelineChartAction
): TimelineChartState {
  switch (action.type) {
    // Define cases for different actions here
    case "ADD_CHART": {
      console.log("Adding or updating chart:", action.chart);
      const existingChartIndex = state.charts.findIndex((chart) => chart.id === action.chart.id);

      if (existingChartIndex !== -1) {
        // Update existing chart
        const updatedCharts = [...state.charts];
        updatedCharts[existingChartIndex] = action.chart;
        return { ...state, charts: updatedCharts, datarevision: state.datarevision + 1 };
      } else {
        // Add new chart
        return {
          ...state,
          charts: [...state.charts, action.chart],
          datarevision: state.datarevision + 1,
        };
      }
    }
    case "REMOVE_CHART": {
      console.log("Removing chart with ID:", action.id);
      // Remove chart by ID, if it exists
      if (state.charts.some((chart) => chart.id === action.id)) {
        const updatedCharts = state.charts.filter((chart) => chart.id !== action.id);
        return { ...state, charts: updatedCharts, datarevision: state.datarevision + 1 };
      }
      return state; // If chart not found, return current state
    }
    case "ADD_VERTICAL_LINE": {
      console.log("Adding vertical line:", action.verticalLine);
      if (state.verticalLines.some((line) => line.id === action.verticalLine.id)) {
        // If the vertical line already exists, do not add it again
        return state;
      }
      return {
        ...state,
        verticalLines: [...state.verticalLines, action.verticalLine],
      };
    }
    case "REMOVE_VERTICAL_LINE": {
      console.log("Removing vertical line with ID:", action.id);
      if (state.verticalLines.some((line) => line.id === action.id)) {
        const updatedVerticalLines = state.verticalLines.filter((line) => line.id !== action.id);
        return { ...state, verticalLines: updatedVerticalLines };
      }
      return state; // If vertical line not found, return current state
    }
    case "SET_LEGEND": {
      console.log("Setting legend:", action.legend);
      // Check whether action.legend is different from the current state
      // It must have the same properties with the same values
      if (
        (state.legend &&
          action.legend &&
          JSON.stringify(state.legend) === JSON.stringify(action.legend)) ||
        (!state.legend && !action.legend)
      ) {
        // No changes, do not update
        return state;
      }
      return { ...state, showlegend: action.legend !== undefined, legend: action.legend };
    }
    default:
      return state;
  }
}
